import axios, { AxiosError } from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// --- PREVIEW RESPONSE (Unchanged) ---
export interface TerminalBookingPreviewResponse {
  success: boolean;
  data: {
    controlNumber: string;
    travelClass: string;
    baggageAllowance: string;
    travellers: Array<{
      firstName: string;
      lastName: string;
      type: "ADT" | "CHD" | "INF";
    }>;
    segments: Array<{
      flightNumber: string;
      origin: string;
      destination: string;
      departureDateTime: string;
      airline: string;
    }>;
  };
}

// --- DETAILED PASSENGER INTERFACE ---
export interface BookingPassengerDetail {
  // Basic Info
  title: string;
  firstName: string;
  lastName: string;
  type: string; // ADT, CHD, INF
  gender: string;
  dateOfBirth: string; // YYYY-MM-DD
  nationality: string; // Country Name or Code

  // Passport Info
  passportNumber: string;
  passportIssuingCountry: string;
  passportIssueDate: string; // YYYY-MM-DD
  passportExpiry: string; // YYYY-MM-DD

  // Financials
  price: string;
  tax: string;
}

export interface BaggageConfig {
  isKg: boolean;
  outbound: {
    ADT: string;
    CHD: string;
    INF: string;
  };
  inbound: {
    ADT: string;
    CHD: string;
    INF: string;
  };
}

// --- MANUAL FLIGHT INTERFACE ---
export interface ManualFlightSegment {
  airlineCode: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureDateTime: string; // ISO String or "YYYY-MM-DDTHH:mm"
  arrivalDateTime: string; // ISO String or "YYYY-MM-DDTHH:mm"
  type: "ob" | "ib"; // Outbound | Inbound

  // Optional fields for richer data
  airlineName?: string;
  rbd?: string;
  travelClass?: string;
  originAirport?: string;
  destinationAirport?: string;
  originCity?: string;
  destinationCity?: string;
  originCountry?: string;
  destinationCountry?: string;
}

// --- PAYLOAD TO BACKEND ---
export interface ConfirmBookingPayload {
  controlNumber: string;
  email: string;
  isGuest: boolean;
  passengerDetails: BookingPassengerDetail[];
  baggageDetails: BaggageConfig;
  totalPrice: number;
  totalTax: number;
  currency: string;

  // Booking Settings
  travelClass: string;
  bookingStatus: "Completed" | "Pending" | "Cancelled";

  // Payment Details (Optional - Only required if status is Completed)
  paidAt?: string; // ISO Date String (YYYY-MM-DD)
  paymentType?: string; // 'Stripe', 'Bank Transfer', etc.

  // ✅ NEW FIELDS FOR NDC / MANUAL ENTRY
  isNDC?: boolean;
  manualFlights?: ManualFlightSegment[];
  agent?: string;
  platform?: string;
  ticketCost?: number;
  issuedFrom?: string;
  agentCategory?: string;
}

/**
 * Step 1: Call backend to preview PNR details (No save yet)
 * Note: This is skipped for NDC Manual bookings in the frontend logic.
 */
export const previewTerminalBooking = async (
  pnr: string,
): Promise<TerminalBookingPreviewResponse> => {
  try {
    const response = await axios.post(
      `${API_URL}/admin/terminal-bookings/preview`,
      { pnr },
    );
    return response.data;
  } catch (error) {
    console.error("Error previewing terminal PNR:", error);
    throw (
      (error as AxiosError)?.response?.data || {
        success: false,
        message: "Failed to fetch PNR details.",
      }
    );
  }
};

/**
 * Step 2: Send confirmed details + manual inputs to save to DB
 */
export const saveTerminalBooking = async (
  data: ConfirmBookingPayload,
): Promise<any> => {
  try {
    const response = await axios.post(
      `${API_URL}/admin/terminal-bookings/confirm`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Error saving terminal booking:", error);
    throw (
      (error as AxiosError)?.response?.data || {
        success: false,
        message: "Failed to save booking.",
      }
    );
  }
};
